import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Cmg1003FileDataQuery,
  Cmg1003LoanQuery,
  Cmg1003PropertyQuery,
  CmgCcwFeeSummaryStore,
  CmgCcwLoanQuery,
  CmgCcwLoanStore,
  CmgCcwPropertyQuery,
  CmgCcwPropertyStore,
  CmgCcwQuery,
  CmgCcwStore,
} from '../../loan-state';
import { FeatureFlagRepository, IntegratedLoginContext, SessionQuery, SessionStore } from '../../core/state';
import { BehaviorSubject, Observable, Subject, catchError, combineLatest, filter, finalize, map, of, take, tap } from 'rxjs';
import { GetCCWPayloadModel } from '../models/get-ccw-payload.model';
import { DialogService } from 'primeng/dynamicdialog';
import { FailedToLoadModalAction, FailedToLoadModalComponent } from '../../core/components';
import {
  AMCRequest,
  AdditionalNotesVM,
  BaseRequestVm,
  ClosingCostWorksheet,
  ClosingCostWorksheetResponseVm,
  FeeSummary,
  FeeWorksheetLoanPropertyInfo,
  FeeWorksheetReportResponseVm,
  Item,
  LePendingResponse,
  LoanBannerVm,
  LoanPropertyPreliminaryDataBase,
  MasterData,
  MilestoneStatus,
  OtherCreditsMasterdata,
  PrepaidDailyInterestVM,
  PrepaidEscrowVm,
  SpanishDocRequest,
  StateCountyMap,
  UpdateDatesRequest,
  UpfrontTypeMasterData,
  VaFeccCalculationData,
  SettingsProviderModel,
  DefaultProvider,
} from '../models/ccw.models';
import {
  BUYDOWNS,
  DISBURSEMENT_SCHED,
  DISBURSEMENT_START,
  ESCROW_TYPE,
  FEESHEET_TYPE,
  OTHER_CREDIT,
  PAID_BY,
  PAID_BY_OTHER_TYPES,
  PAID_TO,
  PREPAID_ESCROW,
  RESPONSIBLE_PARTY,
  UPFRONT_TYPE,
} from '../models/ccw.constants';
import { SelectItem, ToastMessageOptions } from 'primeng/api';
import { SpinnerService } from '../../shared';
import { ErrorNavItem, ErrorNavSection } from '../../shared/components/error-nav/error-nav.models';
import {
  FeeWorksheetMilestone,
  FeeWorkSheetRequestType,
  GetCcwSectionName,
  landSurveyStates,
  LoanPurposeType,
  MortgageType,
} from '../models/ccw.enums';
import { applyTransaction } from '@datorama/akita';
import { CcwPropertyInformationModel } from '../models/ccw-property-information.model';
import { CcwLoanDataModel } from '../models/ccw-loan-data-model';
import { ErrorLinkNavigateToUrla, getErrorByControlId, isEmpty, isFutureDate, SMARTFEES_MAX_SALESPRICE } from '../models/ccw.validations';
import { LoanStatus, MortgageType as LoanMortgageType, OriginationChannel, PurposeOfLoan, RefinancePurposeFha } from '../../cmg-1003/models';
import { REQUIRES_SECURITY_PROFILE } from '../../interceptors';
import { BannerMessages } from '../models/ccw-banner-message.constants';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

@Injectable({ providedIn: 'root' })
export class CCWService {
  private readonly _environment = inject(APP_ENV);
  private readonly _dialogService = inject(DialogService);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _httpClient = inject(HttpClient);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _spinnerService = inject(SpinnerService);
  private readonly _ccwStore = inject(CmgCcwStore);
  private readonly _ccwQuery = inject(CmgCcwQuery);
  private readonly _ccwLoanStore = inject(CmgCcwLoanStore);
  private readonly _ccwLoanQuery = inject(CmgCcwLoanQuery);
  private readonly _ccwPropertyStore = inject(CmgCcwPropertyStore);
  private readonly _ccwPropertyQuery = inject(CmgCcwPropertyQuery);
  private readonly _ccwFeeSummaryStore = inject(CmgCcwFeeSummaryStore);
  private readonly _propertyQueryUrla = inject(Cmg1003PropertyQuery);
  private readonly _loanQueryUrla = inject(Cmg1003LoanQuery);
  private readonly _featureFlagRepo = inject(FeatureFlagRepository);

  closingCostWorksheet$ = new BehaviorSubject<ClosingCostWorksheet>(null);
  closingCostVm: ClosingCostWorksheet; // merge changes into this model which can be used for saving/updating in the CCW.

  triggerSaveAndGetMonthsOfReserves$ = new Subject<boolean>();

  loanBanner$ = new BehaviorSubject<LoanBannerVm>(null);
  errorMessage$ = new Subject<ToastMessageOptions>();
  resetForms$ = new Subject<ClosingCostWorksheet>();
  resetFormsTouched$ = new Subject<boolean>();
  formsDirty$ = new BehaviorSubject<boolean>(false);
  saveCcw$ = new Subject<void>();
  activeTab$ = new BehaviorSubject<number | null>(null);

  set formsDirty(value: boolean) {
    this.formsDirty$.next(value);
  }

  get paidByOtherTypes(): SelectItem[] {
    return PAID_BY_OTHER_TYPES.map((x) => {
      return { label: x.name, value: x.id, disabled: x.id !== 0 };
    });
  }
  get paidToMasterData(): MasterData[] {
    return PAID_TO as MasterData[];
  }
  get responsibleParty(): MasterData[] {
    return RESPONSIBLE_PARTY as MasterData[];
  }
  get paidByMasterData(): MasterData[] {
    return PAID_BY as MasterData[];
  }
  get escrowTypeList(): Item[] {
    return ESCROW_TYPE as Item[];
  }
  get prepaidEscrowMasterData(): MasterData[] {
    return PREPAID_ESCROW as MasterData[];
  }

  get disbursementSchedList(): MasterData[] {
    return DISBURSEMENT_SCHED as MasterData[];
  }

  get disbursementStartList(): MasterData[] {
    return DISBURSEMENT_START as MasterData[];
  }

  get feesheetType(): SelectItem[] {
    return FEESHEET_TYPE.map((x) => {
      return { label: x.name, value: x.id, disabled: x.disabled };
    });
  }

  get upfrontType(): UpfrontTypeMasterData[] {
    return UPFRONT_TYPE as UpfrontTypeMasterData[];
  }

  get buyDowns(): MasterData[] {
    return BUYDOWNS as MasterData[];
  }

  get otherCredit(): OtherCreditsMasterdata[] {
    return OTHER_CREDIT as OtherCreditsMasterdata[];
  }

  get disbursementStartYearList(): MasterData[] {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let i = 0; i < 6; i++) {
      years.push({ id: currentYear + i, name: currentYear + i });
    }

    return years;
  }

  get ccwPayloadModel(): GetCCWPayloadModel {
    this._ccwPayloadModel = {
      userId: this._userId,
      loanId: this._loanId,
      channel: this._originationChannel,
      fileDataId: this._fileDataId,
      fileName: this._fileName,
      organizationId: this._organizationId,
      retailUserName: this._userName,

      // NOTE: Clear 1 API code expects these three parameters, but it immediately overrides them with values from AppSettings.
      compCertCategoryCode: '',
      compCertTypeCode: '',
      ccBackgroundServiceFlag: false,
    };
    return this._ccwPayloadModel;
  }

  escrowFee1Updated: Subject<PrepaidEscrowVm> = new Subject<PrepaidEscrowVm>();
  escrowFee2Updated: Subject<PrepaidEscrowVm> = new Subject<PrepaidEscrowVm>();
  validationErrors$: BehaviorSubject<ErrorNavSection[]> = new BehaviorSubject<ErrorNavSection[]>([]);
  validationCalled$: Subject<boolean> = new Subject<boolean>();

  totalClosingCost$: BehaviorSubject<number> = new BehaviorSubject(0);
  set totalClosingCost(value: number) {
    this.totalClosingCost$.next(value);
  }
  prepaidDailyInterest$: BehaviorSubject<PrepaidDailyInterestVM> = new BehaviorSubject(null);
  feeSummaryIsTouched$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private _backupClosingCostVm: ClosingCostWorksheet;
  private _ccwPayloadModel: GetCCWPayloadModel;
  private _userId: number;
  private _userName: string;
  private _fileDataId: number;
  private _fileName: string;
  private _loanId: number;
  private _organizationId: number;
  private _originationChannel: OriginationChannel;

  disabledStatuses: LoanStatus[] = [
    LoanStatus.Funded,
    LoanStatus.Purchased,
    LoanStatus.InvestorPurchased,
    LoanStatus.Shipped,
    LoanStatus.PostClosingCompleted,
    LoanStatus.Withdrawn,
    LoanStatus.Declined,
  ];
  isCcwDisabled$ = this._sessionQuery.select((s) => s.loanStatusId).pipe(map((s) => this.disabledStatuses.includes(s)));

  isStateDirty$ = combineLatest([this._ccwLoanQuery.dirtyChecker2.isDirty$, this._ccwPropertyQuery.dirtyChecker2.isDirty$]).pipe(
    map(([isCcwLoanDirty, isCcwPropertyDirty]) => {
      return isCcwLoanDirty || isCcwPropertyDirty;
    }),
  );

  isLoanAioNonConstruction$ = this._ccwLoanQuery.select().pipe(
    map((loan) => {
      const code = loan?.loanProgramCode?.toLocaleLowerCase();
      return code?.startsWith('801') && !code?.includes('1xc');
    }),
  );

  smartFeeValidations$: Observable<string[]> = this._ccwLoanQuery.selectLoanInfo().pipe(
    map((loan) => {
      const validations = [];
      // Loan Purpose Checks
      if (
        ([PurposeOfLoan.construction, PurposeOfLoan.constructionPerm, PurposeOfLoan.refiSecond, PurposeOfLoan.refiThird].includes(
          loan?.loanPurpose,
        ) &&
          ![LoanMortgageType.va, LoanMortgageType.other].includes(loan?.mortgageAppliedFor)) ||
        (loan?.mortgageAppliedFor === LoanMortgageType.va && loan?.loanPurpose === PurposeOfLoan.constructionPerm) ||
        loan?.loanPurpose === PurposeOfLoan.other
      ) {
        validations.push(BannerMessages.UNSUPPORTED_LOANPURPOSE);
      }

      // Refinance Checks
      if (loan?.mortgageAppliedFor === LoanMortgageType.fha && loan?.refinancePurposeFha === RefinancePurposeFha.SimpleRefinance) {
        validations.push(BannerMessages.UNSUPPORTED_REFITYPE);
      }

      // Sales Price / Estimated Value Checks
      if (loan?.salesPrice > SMARTFEES_MAX_SALESPRICE && loan?.loanPurpose === PurposeOfLoan.purchase) {
        validations.push(BannerMessages.UNSUPPORTED_SALESPRICE);
      }

      return validations;
    }),
  );

  getClosingCostWorksheet(callbackFn?: any): void {
    combineLatest({
      session: this._sessionQuery.select((s) => {
        return { all1003Loaded: s.all1003Loaded, userId: s.userId, userName: s.userName };
      }),
      fileData: this._fileDataQuery.select((f) => {
        return { fileDataId: f.fileDataId, fileName: f.fileName, loanID: f.loanID, organizationId: f.organizationId };
      }),
      originationChannel: this._loanQueryUrla.select((o) => o.originationChannel),
    })
      .pipe(
        filter(({ session }) => {
          return session.all1003Loaded;
        }),
        take(1),
        tap(({ session, fileData, originationChannel }) => {
          this._userId = session.userId;
          this._userName = session.userName;
          this._fileDataId = fileData.fileDataId;
          this._fileName = fileData.fileName;
          this._loanId = +fileData.loanID;
          this._organizationId = fileData.organizationId;
          this._originationChannel = originationChannel;
        }),
      )
      .subscribe(() => {
        this._getClosingCostWorksheet()
          .pipe(
            filter((ccw) => !!ccw),
            take(1),
          )
          .subscribe((ccw) => {
            // Store setup
            this.resetStores();
            this._initStores(ccw);

            this.closingCostVm = ccw;
            this._backupClosingCostVm = JSON.parse(JSON.stringify(ccw));
            this.closingCostWorksheet$.next(ccw);

            if (callbackFn && typeof callbackFn === 'function') {
              callbackFn();
            }
          });
      });
  }

  getMappedLoanPropertyInfo(): FeeWorksheetLoanPropertyInfo {
    return {
      ...this.closingCostVm?.feeWorksheetLoanPropertyInfo,
      ...this._ccwQuery.mapUrlaLoanToCcw(this._ccwLoanQuery.getValue(), this._ccwPropertyQuery.getValue()),
    };
  }

  saveClosingCostWorksheet(requestType: FeeWorkSheetRequestType): Observable<ClosingCostWorksheetResponseVm> {
    this._spinnerService.show();
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/SaveClosingCostWorksheet`;
    const payload = this.closingCostVm;
    const isIntegratedLogin = this._sessionStore.getValue()?.integratedLoginContext === IntegratedLoginContext.Byte;

    // Apply values from URLA Loan and Property Stores
    payload.feeWorksheetLoanPropertyInfo = this.getMappedLoanPropertyInfo();
    payload.feeWorksheetServiceRequestType = requestType;
    const ccwLoan = this._ccwLoanQuery.getValue();
    payload.onTimeClosing = ccwLoan.onTimeClosing;

    return this._httpClient.post<ClosingCostWorksheetResponseVm>(url, payload).pipe(
      catchError((error) => {
        // eslint-disable-next-line
        console.error(error);
        const correlationId = error.headers?.get('X-Plinth-Reqtraceid') ? `Correlation ID: ${error.headers?.get('X-Plinth-Reqtraceid')}` : '';
        const correlationDiv = correlationId ? `<div class="correlation mt-3 mb-3">${correlationId}</div>` : '<br/>';
        const errorsList = this.getErrorResponseList(error);
        const header = 'Closing Cost Worksheet Failed To Save Correctly';

        this._dialogService.open(FailedToLoadModalComponent, {
          header: header,
          width: '512px',
          data: {
            message: correlationDiv + errorsList,
            action: isIntegratedLogin ? FailedToLoadModalAction.None : FailedToLoadModalAction.GoToPipeline,
            fileDataId: payload.fileDataId,
            loanId: payload.fileName,
          },
          closable: false,
          draggable: true,
          keepInViewport: true,
          styleClass: 'dialog-content-overflow-hidden',
        });

        this.errorMessage$.next({
          severity: 'error',
          detail: `${header}. ${correlationDiv}`,
        });

        return of(null);
      }),
      finalize(() => {
        this._spinnerService.hide();
      }),
    );
  }

  saveLoanPropertyPreliminaryData(payload: LoanPropertyPreliminaryDataBase): Observable<any> {
    this._spinnerService.show();
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/SaveLoanPropertyPreliminaryData`;
    const isIntegratedLogin = this._sessionStore.getValue()?.integratedLoginContext === IntegratedLoginContext.Byte;

    return this._httpClient.post<any>(url, payload).pipe(
      catchError((error) => {
        // eslint-disable-next-line
        console.error(error);
        const correlationId = error.headers?.get('X-Plinth-Reqtraceid') ? `Correlation ID: ${error.headers?.get('X-Plinth-Reqtraceid')}` : '';
        const correlationDiv = correlationId ? `<div class="correlation mt-3 mb-3">${correlationId}</div>` : '<br/>';
        const header = 'Closing Cost Loan and Property Preliminary Data Failed To Save Correctly';

        this._dialogService.open(FailedToLoadModalComponent, {
          header: header,
          width: '512px',
          data: {
            message: correlationDiv,
            action: isIntegratedLogin ? FailedToLoadModalAction.None : FailedToLoadModalAction.GoToPipeline,
            fileDataId: payload.fileDataId,
            loanId: this.closingCostVm?.fileName,
          },
          closable: false,
          draggable: true,
          keepInViewport: true,
          styleClass: 'dialog-content-overflow-hidden',
        });

        this.errorMessage$.next({
          severity: 'error',
          detail: `${header}. ${correlationDiv}`,
        });

        return of(null);
      }),
      finalize(() => {
        this._spinnerService.hide();
      }),
    );
  }

  updateImportantDates(payload: UpdateDatesRequest): Observable<LePendingResponse> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/UpdateImportantDates`;
    return this._httpClient.post<LePendingResponse>(url, payload).pipe(
      tap(() => {
        this._backupClosingCostVm.feeWorksheetLoanPropertyInfo = { ...this._backupClosingCostVm.feeWorksheetLoanPropertyInfo, ...payload };
      }),
    );
  }

  addNewNote(payload: AdditionalNotesVM): Observable<LePendingResponse> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/PostNotes`;
    return this._httpClient.post<LePendingResponse>(url, payload);
  }

  requestSpanishDoc(payload: SpanishDocRequest): Observable<LePendingResponse> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/SpanishDocRequest`;
    return this._httpClient.post<LePendingResponse>(url, payload);
  }

  generatePDF(payload: ClosingCostWorksheet): Observable<FeeWorksheetReportResponseVm> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/GetFeeWorksheetReport`;
    return this._httpClient.post<FeeWorksheetReportResponseVm>(url, payload);
  }

  checkPDFFileExists(fileDataId: string, fileName: string, guid: string): Observable<boolean> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/ReportExist/${fileDataId}/${fileName}/${guid}`;
    return this._httpClient.get<boolean>(url);
  }

  viewReport(filename: string, fileDataId: string, guid: string): Observable<any> {
    const url: string = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/viewReport?fileName=${filename}&fileDataId=${fileDataId}&guid=${guid}`;

    return this._httpClient.get(url, { responseType: 'blob' as 'json' }).pipe(
      tap((response) => {
        const dataType = response.type;
        const binaryData = [];
        binaryData.push(response);

        const href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
        window.open(href, '_blank');
      }),
    );
  }

  getStateCountyFips(): Observable<StateCountyMap[]> {
    const url: string = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/statecounty`;
    return this._httpClient.get<StateCountyMap[]>(url);
  }

  updateSavedMilestoneStatus(request: MilestoneStatus): Observable<any> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/UpdateMilestone`;
    return this._httpClient.post<any>(url, request);
  }

  updateAMC(request: AMCRequest): Observable<any> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/UpdateAMC`;
    return this._httpClient.post<any>(url, request);
  }

  updateIntegratedError(error: ErrorNavItem): void {
    if (this._sessionStore.getValue()?.integratedLoginContext === IntegratedLoginContext.Byte) {
      if (error && error?.errorLink === ErrorLinkNavigateToUrla) {
        error.controlId = null;
        error.errorText = error?.errorText?.replace('URLA', 'Home');
        error.errorLink = null;
      }
    }
  }

  addValidationError(section: string, controlId: string, controlName?: string, errorText?: string, errorLink?: string): void {
    this.validationErrors$.pipe(take(1)).subscribe((errors) => {
      const existingSection = errors.findIndex((e) => e.section === section);
      const newError = { controlId, controlName, errorText, errorLink };
      this.updateIntegratedError(newError);

      if (newError.errorLink === ErrorLinkNavigateToUrla) {
        newError.errorLink = `/urla2/${this.closingCostVm?.fileDataId}`;
      }
      if (existingSection > -1) {
        if (errors[existingSection]?.errors) {
          errors[existingSection].errors = [...errors[existingSection].errors, newError];
        }
      } else {
        errors.push({ section: section, errors: [newError] });
      }
      this.validationErrors$.next(errors);
    });
  }

  addValidationErrorRequired(section: string, controlId: string): void {
    this.validationErrors$.pipe(take(1)).subscribe((errors) => {
      const existingSection = errors.findIndex((e) => e.section === section);
      const newError = getErrorByControlId(section, controlId);
      this.updateIntegratedError(newError);

      if (newError.errorLink === ErrorLinkNavigateToUrla) {
        newError.errorLink = `/urla2/${this.closingCostVm?.fileDataId}`;
      }
      if (existingSection > -1) {
        if (errors[existingSection]?.errors) {
          errors[existingSection].errors = [...errors[existingSection].errors, newError];
        }
      } else {
        errors.push({ section: section, errors: [newError] });
      }
      this.validationErrors$.next(errors);
    });
  }

  resetValidationErrors(section?: string): void {
    if (!section) {
      this.validationErrors$.next([]);
    } else {
      this.validationErrors$.pipe(take(1)).subscribe((errors) => {
        this.validationErrors$.next(errors.filter((e) => e.section !== section));
      });
    }
  }

  validateClosingCostWorksheet(runAll: boolean = false): ErrorNavSection[] {
    this.resetValidationErrors();

    if (!runAll) {
      switch (this.closingCostVm.mileStoneStatus) {
        case FeeWorksheetMilestone.LoanPropertySection:
          this.validateLoanAndPropertyInfo();
          break;
        case FeeWorksheetMilestone.TaxQuestionSection:
          this.validateTaxQuestion();
          break;
        case FeeWorksheetMilestone.LandServiceSection:
          this.validateLandService();
          break;
        default:
          this.validateLoanAndPropertyInfo();
          break;
      }
    } else {
      if (this.closingCostVm.feeWorksheetLoanPropertyInfo) {
        this.validateLoanAndPropertyInfo();
      }

      if (this.closingCostVm.taxQuestion) {
        this.validateTaxQuestion();
      }

      if (this.closingCostVm.landSurveyServiceProviderSelected) {
        this.validateLandService();
      }
    }

    this.validationCalled$.next(true);
    return this.validationErrors$.getValue();
  }

  validateSeviceWorkFlowClosingCostWorksheet(): ErrorNavSection[] {
    this.resetValidationErrors();
    switch (this.closingCostVm.mileStoneStatus) {
      case FeeWorksheetMilestone.TitleServiceSection:
        this.validateTitleService();
        break;
      case FeeWorksheetMilestone.SettlementServiceSection:
        this.validateSettlementService();
        break;
      case FeeWorksheetMilestone.PestServiceSection:
        this.validatePestService();
        break;
      case FeeWorksheetMilestone.StructuralEngineeringSection:
        this.validateStructuralEngineeringService();
        break;
    }
    this.validationCalled$.next(true);
    return this.validationErrors$.getValue();
  }

  validateLoanAndPropertyInfo(): void {
    const section = GetCcwSectionName(FeeWorksheetMilestone.LoanPropertySection);
    const info = this.getMappedLoanPropertyInfo();

    info.loanPurpose === LoanPurposeType.Other && isEmpty(info.loanPurposeOther) && this.addValidationErrorRequired(section, 'loanpurposedesc');
    !info.mortgageType && this.addValidationErrorRequired(section, 'mortgagetype');

    !info.loanPurpose && this.addValidationErrorRequired(section, 'loanPurpose');
    [PurposeOfLoan.construction, PurposeOfLoan.constructionPerm, PurposeOfLoan.refiSecond, PurposeOfLoan.refiThird].findIndex(
      (p) => info.loanPurpose === p,
    ) > -1 &&
      this.addValidationError(section, 'loanPurpose', 'Purpose of Loan', 'Value not supported in Smart Fees. Go to URLA.', ErrorLinkNavigateToUrla);

    isEmpty(info.loanProgramCode) && this.addValidationErrorRequired(section, 'loanprogramcode');
    isEmpty(info.loanProgramName) && this.addValidationErrorRequired(section, 'loanprogramname');

    ((info.loanPurpose === LoanPurposeType.Refinance &&
      info.mortgageType !== MortgageType.Va &&
      info.mortgageType !== MortgageType.Fha &&
      !info.refiPurpAU) ||
      (info.loanPurpose === LoanPurposeType.Refinance && info.mortgageType === MortgageType.Va && !info.refiTypeVA) ||
      (info.loanPurpose === LoanPurposeType.Refinance && info.mortgageType === MortgageType.Fha && !info.refiTypeFHA)) &&
      this.addValidationErrorRequired(section, 'typeOfRefi');

    info.mortgageType === MortgageType.Fha &&
      info.loanPurpose === LoanPurposeType.Refinance &&
      info.refiTypeFHA === RefinancePurposeFha.SimpleRefinance &&
      this.addValidationError(
        section,
        'refinancePurposeFha',
        'Refinance Type',
        'Value not supported in Smart Fees. Go to URLA.',
        ErrorLinkNavigateToUrla,
      );

    info.mortgageType === MortgageType.Fha && !info.sectionOfAct && this.addValidationErrorRequired(section, 'sectionofact');

    info.mortgageType === MortgageType.Va &&
      (info.veteranHasDisability === 1 || info.everHadVALoan === 1 || info.everHadVALoan === 2) &&
      !info.fundingFeeType &&
      this.addValidationErrorRequired(section, 'fundingfeetype');

    !info.baseLoan && this.addValidationErrorRequired(section, 'baseLoanAmount');

    !(info.loanPurpose === LoanPurposeType.Refinance || info.loanPurpose === LoanPurposeType.ConstructionPerm) &&
      (!info.salesPrice || info.salesPrice < 1) &&
      this.addValidationErrorRequired(section, 'salesPrice');

    info.loanPurpose === LoanPurposeType.Purchase &&
      info.salesPrice &&
      info.salesPrice > SMARTFEES_MAX_SALESPRICE &&
      this.addValidationError(section, 'salesPrice', 'Sales Price', 'Value not supported in Smart Fees.');

    !(info.loanPurpose === LoanPurposeType.Refinance || info.loanPurpose === LoanPurposeType.ConstructionPerm) &&
      (!info.appraisedValue || info.appraisedValue < 1) &&
      this.addValidationErrorRequired(section, 'appraisalValue');

    info.loanPurpose === LoanPurposeType.Refinance &&
      info.existingSecondLien === 1 &&
      !info.subordinateAmount &&
      this.addValidationErrorRequired(section, 'subfibaseloan');

    !info.intRate && this.addValidationErrorRequired(section, 'interestRate');

    info.mortgageType === MortgageType.Conventional &&
      (!info.fileCreditScore || info.fileCreditScore < 300 || info.fileCreditScore > 850) &&
      this.addValidationErrorRequired(section, 'fileCreditScore');

    info.mortgageType === MortgageType.Usda &&
      (info.rhsCaseNoAssignmentPeriod === null || info.rhsCaseNoAssignmentPeriod === undefined) &&
      this.addValidationErrorRequired(section, 'usdaassigndate');

    !info.property && this.addValidationErrorRequired(section, 'propertyType');

    info.loanPurpose === LoanPurposeType.Refinance &&
      (isEmpty(info.subPropStreet) || info.subPropStreet?.toLowerCase().trim() === 'tbd') &&
      this.addValidationErrorRequired(section, 'street');

    isEmpty(info.subPropCityName) && this.addValidationErrorRequired(section, 'city');
    isEmpty(info.subPropStateName) && this.addValidationErrorRequired(section, 'state');
    isEmpty(info.subPropPostalCode) && this.addValidationErrorRequired(section, 'zip');
    (isEmpty(info.subPropCounty) || info.subPropCounty === 'Select') && this.addValidationErrorRequired(section, 'county');
    isEmpty(info.rushAppraisal) && this.addValidationErrorRequired(section, 'rushAppraisal');
    isEmpty(info.finalOrReinspection1004dRequired) && this.addValidationErrorRequired(section, 'finalOrReinspection1004dRequired');
    !info.occupancy && this.addValidationErrorRequired(section, 'occupancy');
    !info.noOfUnits && this.addValidationErrorRequired(section, 'numberOfUnits');
    !info.sizeInAcres && this.addValidationErrorRequired(section, 'sizeInAcres');
    (isEmpty(info.loCommunicationToDisclosuresAmcValue) || info.loCommunicationToDisclosuresAmcValue === 'Select') &&
      this.addValidationErrorRequired(section, 'loCommunicationToDisclosuresAmcValue');
    isEmpty(info.jumboRequiredSecondAppraisal) && this.addValidationErrorRequired(section, 'jumboRequiredSecondAppraisal');
    isEmpty(info.isLoanANewConstruction) && this.addValidationErrorRequired(section, 'isLoanANewConstruction');
    info.loCommunicationToDisclosuresAmcValue === 'PIW-No Appraisal Fee' &&
      info.appraisalWaiver === false &&
      this.addValidationErrorRequired(section, 'appraisalWaiver');
    isEmpty(info.specialFloodHazardArea) && this.addValidationErrorRequired(section, 'floodZone');
    !(info.daysOfPerDiem <= 32) && this.addValidationErrorRequired(section, 'daysOfPerDiem');
    (!info.loanEstimatedClosingDate || (info.loanEstimatedClosingDate && !isFutureDate(info.loanEstimatedClosingDate))) &&
      this.addValidationErrorRequired(section, 'loanEstimatedClosingDate');
    (!info.loanFirstPaymentDate || (info.loanFirstPaymentDate && !isFutureDate(info.loanFirstPaymentDate))) &&
      this.addValidationErrorRequired(section, 'loanFirstPaymentDate');
  }

  validateTaxQuestion(): void {
    const section = GetCcwSectionName(FeeWorksheetMilestone.TaxQuestionSection);
    const taxQuestion = this.closingCostVm?.taxQuestion;

    if (taxQuestion && taxQuestion?.feeWorksheetTaxQuestionsPayLoad && taxQuestion.feeWorksheetTaxQuestionsPayLoad.length > 0) {
      taxQuestion.feeWorksheetTaxQuestionsPayLoad.forEach((question) => {
        if (question.important && isEmpty(question.value) && question.id === 'qclaim') {
          this.addValidationError(section, question.id, 'Tax Question Required', question.name);
        }
      });
    }
  }

  validateLandService(): void {
    let landServiceProviderRequired = true;

    if (
      !this.closingCostVm.landSurveyServiceProvider?.feeWorksheetServiceProviders ||
      this.closingCostVm.landSurveyServiceProvider?.feeWorksheetServiceProviders?.length === 0
    ) {
      landServiceProviderRequired = false;
    }

    if (landServiceProviderRequired) {
      const section = GetCcwSectionName(FeeWorksheetMilestone.LandServiceSection);
      const info = this.getMappedLoanPropertyInfo();
      const surveyStates = landSurveyStates;
      if (!this.closingCostVm.landSurveyServiceProviderSelected && info.subPropStateName && surveyStates.includes(info.subPropStateName)) {
        this.addValidationErrorRequired(section, 'landServiceControlId');
      }
    }
  }

  validateTitleService(): void {
    let titleServiceProviderRequired = true;

    if (
      !this.closingCostVm.titleServiceProvider?.feeWorksheetServiceProviders ||
      this.closingCostVm.titleServiceProvider?.feeWorksheetServiceProviders?.length === 0
    ) {
      titleServiceProviderRequired = false;
    }

    if (titleServiceProviderRequired) {
      const section = GetCcwSectionName(FeeWorksheetMilestone.TitleServiceSection);
      if (!this.closingCostVm.titleServiceProviderSelected) {
        this.addValidationErrorRequired(section, 'titleServiceControlId');
      }
    }
  }

  validatePestService(): void {
    let pestServiceProviderRequired = true;

    if (
      !this.closingCostVm.pestServiceProvider?.feeWorksheetServiceProviders ||
      this.closingCostVm.pestServiceProvider?.feeWorksheetServiceProviders?.length === 0
    ) {
      pestServiceProviderRequired = false;
    }

    if (pestServiceProviderRequired) {
      const section = GetCcwSectionName(FeeWorksheetMilestone.PestServiceSection);
      if (!this.closingCostVm.pestServiceProviderSelected) {
        this.addValidationErrorRequired(section, 'pestServiceControlId');
      }
    }
  }

  validateSettlementService(): void {
    let settlementServiceProviderRequired = true;

    if (
      !this.closingCostVm.settlementServiceProvider?.feeWorksheetServiceProviders ||
      this.closingCostVm.settlementServiceProvider?.feeWorksheetServiceProviders?.length === 0
    ) {
      settlementServiceProviderRequired = false;
    }

    if (settlementServiceProviderRequired) {
      const section = GetCcwSectionName(FeeWorksheetMilestone.SettlementServiceSection);
      if (!this.closingCostVm.settlementServiceProviderSelected) {
        this.addValidationErrorRequired(section, 'settlementServiceControlId');
      }
    }
  }

  validateStructuralEngineeringService(): void {
    let engineeringServiceProviderRequired = true;

    if (
      !this.closingCostVm.structuralEngineeringServiceProvider?.feeWorksheetServiceProviders ||
      this.closingCostVm.structuralEngineeringServiceProvider?.feeWorksheetServiceProviders?.length === 0
    ) {
      engineeringServiceProviderRequired = false;
    }

    if (engineeringServiceProviderRequired) {
      const section = GetCcwSectionName(FeeWorksheetMilestone.StructuralEngineeringSection);
      if (!this.closingCostVm.structuralEngineeringServiceProviderSelected) {
        this.addValidationErrorRequired(section, 'structuralEngineeringServiceControlId');
      }
    }
  }

  getBannerLoanInfo() {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/GetBannerInfo`;
    const activeLoanRequest: BaseRequestVm = {
      channel: this._originationChannel?.toString(),
      fileDataId: this._fileDataId.toString(),
      fileName: this._fileName,
      loanId: this._loanId.toString(),
      organizationId: this._organizationId.toString(),
      userId: this._userId.toString(),
      retailUserName: this._userName,
      userRole: '',
    };

    return this._httpClient.post<LoanBannerVm>(url, activeLoanRequest).pipe(
      tap((banner) => {
        this.loanBanner$.next(banner);
      }),
    );
  }

  getErrorResponseList(response: HttpErrorResponse): string {
    let list = '';
    const errors = response?.error?.errors;
    if (errors && Object.keys(errors)?.length > 0) {
      list = '<ul>';
      Object.keys(errors).forEach((key) => {
        const fieldErrors = errors[key];
        if (fieldErrors && fieldErrors?.length > 0) {
          fieldErrors.forEach((error) => {
            list += `<li>${error}</li>`;
          });
        }
      });
      list += '</ul>';
    }
    return list;
  }

  resetStores(): void {
    applyTransaction(() => {
      this._ccwStore.reset();
      this._ccwLoanStore.reset(true);
      this._ccwPropertyStore.reset(true);
      this._ccwFeeSummaryStore.reset();
    });

    this.closingCostWorksheet$.next(null);
    this.closingCostVm = null;
  }

  resetCCWForms() {
    const ccw = JSON.parse(JSON.stringify(this._backupClosingCostVm));
    this.resetStores();
    this._initStores(ccw);
    this.closingCostVm = ccw;
    this.closingCostWorksheet$.next(ccw);
    this.resetForms$.next(this.closingCostVm);
  }

  resetFormsTouched() {
    this.resetFormsTouched$.next(true);
  }

  postMonthsOfReserves(fileDataId: number) {
    this._spinnerService.show();
    const url = `${this._environment.retail.apiUrl}/feeWorksheet/loanfile/${fileDataId}/MonthsOfReserves`;
    return this._httpClient.post(url, null).pipe(
      finalize(() => {
        this._spinnerService.hide();
      }),
    );
  }

  private _getClosingCostWorksheet() {
    this._spinnerService.show();
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/GetClosingCostWorksheet`;
    const payload = this.ccwPayloadModel;
    const isIntegratedLogin = this._sessionStore.getValue()?.integratedLoginContext === IntegratedLoginContext.Byte;
    return this._httpClient
      .post<ClosingCostWorksheet>(url, payload, {
        context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
      })
      .pipe(
        catchError((error) => {
          // eslint-disable-next-line
          console.error(error);
          const correlationId = error.headers?.get('X-Plinth-Reqtraceid') ? `Correlation ID: ${error.headers?.get('X-Plinth-Reqtraceid')}` : '';
          const correlationDiv = correlationId ? `<div class="correlation mt-3 mb-3">${correlationId}</div>` : '<br/>';
          const header = 'Closing Cost Worksheet Failed To Load Correctly';

          this._dialogService.open(FailedToLoadModalComponent, {
            header: header,
            width: '512px',
            data: {
              message: correlationDiv,
              action: isIntegratedLogin ? FailedToLoadModalAction.None : FailedToLoadModalAction.GoToPipeline,
              fileDataId: payload.fileDataId,
              loanId: payload.fileName,
            },
            closable: false,
            draggable: true,
            keepInViewport: true,
            styleClass: 'dialog-content-overflow-hidden',
          });

          this.errorMessage$.next({
            severity: 'error',
            detail: `${header}. ${correlationDiv}`,
          });

          return of(null);
        }),
        finalize(() => {
          this._spinnerService.hide();
        }),
      );
  }

  private _initStores(ccw: ClosingCostWorksheet): void {
    combineLatest([this._loanQueryUrla.selectLoanInfo(), this._propertyQueryUrla.select()])
      .pipe(take(1))
      .subscribe(([loanInfoUrla, propertyInfoUrla]) => {
        applyTransaction(() => {
          this._ccwStore.preInitialize(ccw);

          const ccwLoanInfoModel: CcwLoanDataModel = Object.assign({}, loanInfoUrla);
          ccwLoanInfoModel.loanProgramCode = ccw.feeWorksheetLoanPropertyInfo.loanProgramCode;
          ccwLoanInfoModel.loanProgramName = ccw.feeWorksheetLoanPropertyInfo.loanProgramName;
          ccwLoanInfoModel.loanPurpose = ccw.feeWorksheetLoanPropertyInfo.loanPurpose ?? null;
          ccwLoanInfoModel.appraisalValue = ccw.feeWorksheetLoanPropertyInfo.appraisedValue ?? null;
          ccwLoanInfoModel.salesPrice = ccw.feeWorksheetLoanPropertyInfo.salesPrice ?? null;
          ccwLoanInfoModel.baseLoanAmount = ccw.feeWorksheetLoanPropertyInfo.baseLoan ?? null;
          ccwLoanInfoModel.interestRate = ccw.feeWorksheetLoanPropertyInfo.intRate ?? null;
          ccwLoanInfoModel.interestOnlyPeriod = loanInfoUrla.interestOnlyPeriod;
          ccwLoanInfoModel.currentUnpaidPrincipleBalanceamount = ccw.feeWorksheetLoanPropertyInfo.currentUnpaidPrincipleBalanceamount;
          ccwLoanInfoModel.secondSubordinateAmount = ccw.feeWorksheetLoanPropertyInfo.secondSubordinateAmount;
          ccwLoanInfoModel.thirdSubordinateAmount = ccw.feeWorksheetLoanPropertyInfo.thirdSubordinateAmount;
          ccwLoanInfoModel.fileCreditScore = ccw.feeWorksheetLoanPropertyInfo.fileCreditScore ?? null;
          ccwLoanInfoModel.lockFloating = ccw.feeWorksheetLoanPropertyInfo.lockFloating;
          ccwLoanInfoModel.specialFloodHazardArea = ccw.feeWorksheetLoanPropertyInfo.specialFloodHazardArea;
          ccwLoanInfoModel.loanCommitmentDate = ccw.feeWorksheetLoanPropertyInfo.loanCommitmentDate;
          ccwLoanInfoModel.onTimeClosing = ccw.onTimeClosing;
          ccwLoanInfoModel.loanEstimatedClosingDate = ccw.feeWorksheetLoanPropertyInfo.loanEstimatedClosingDate;
          ccwLoanInfoModel.loanEstimatedFundingDate = ccw.feeWorksheetLoanPropertyInfo.loanEstimatedFundingDate;
          ccwLoanInfoModel.loanFirstPaymentDate = ccw.feeWorksheetLoanPropertyInfo.loanFirstPaymentDate;
          ccwLoanInfoModel.estClosingDateStringFormat = ccw.feeWorksheetLoanPropertyInfo.estClosingDateStringFormat;
          ccwLoanInfoModel.estFundingDateStringFormat = ccw.feeWorksheetLoanPropertyInfo.estFundingDateStringFormat;
          ccwLoanInfoModel.firstPaymentDateStringFormat = ccw.feeWorksheetLoanPropertyInfo.firstPaymentDateStringFormat;
          ccwLoanInfoModel.daysOfPerDiem = ccw.feeWorksheetLoanPropertyInfo.daysOfPerDiem;
          ccwLoanInfoModel.dailyInterest = ccw.feeWorksheetLoanPropertyInfo.dailyInterest;
          ccwLoanInfoModel.upfrontMiType = ccw.feeWorksheetLoanPropertyInfo.upfrontMiType;
          ccwLoanInfoModel.monthlyMiFactor = ccw.feeWorksheetLoanPropertyInfo.monthlyMIFactor;
          ccwLoanInfoModel.monthlyMiAmount = ccw.feeWorksheetLoanPropertyInfo.monthlyMIAmount;
          ccwLoanInfoModel.miPaidInCash = ccw.feeWorksheetLoanPropertyInfo.miPaidInCash;
          ccwLoanInfoModel.upfrontMiFactor = ccw.feeWorksheetLoanPropertyInfo.upfrontMIFactor;
          ccwLoanInfoModel.upfrontMiAmount = ccw.feeWorksheetLoanPropertyInfo.upfrontMiAmount;
          ccwLoanInfoModel.dpfactor = ccw.feeWorksheetLoanPropertyInfo.dpfactor;
          this._ccwLoanStore.preInitialize(ccwLoanInfoModel);

          const ccwPropertyInfoModel: CcwPropertyInformationModel = propertyInfoUrla;
          ccwPropertyInfoModel.sizeInAcres = ccw.feeWorksheetLoanPropertyInfo.sizeInAcres;
          ccwPropertyInfoModel.finalOrReinspection1004dRequired = ccw.feeWorksheetLoanPropertyInfo.finalOrReinspection1004dRequired ?? null;
          ccwPropertyInfoModel.rushAppraisal = ccw.feeWorksheetLoanPropertyInfo.rushAppraisal ?? null;
          ccwPropertyInfoModel.jumboRequiredSecondAppraisal = ccw.feeWorksheetLoanPropertyInfo.jumboRequiredSecondAppraisal ?? null;
          ccwPropertyInfoModel.isLoanANewConstruction = ccw.feeWorksheetLoanPropertyInfo.isLoanANewConstruction ?? null;
          ccwPropertyInfoModel.appraisalWaiver = ccw.feeWorksheetLoanPropertyInfo.appraisalWaiver;
          ccwPropertyInfoModel.loCommunicationToDisclosuresAmcValue = ccw.feeWorksheetLoanPropertyInfo.loCommunicationToDisclosuresAmcValue;

          this._ccwPropertyStore.preInitialize(propertyInfoUrla);
          this._ccwFeeSummaryStore.preInitialize(ccw?.feeSummary);
        });
      });
  }

  getVAFeccCalculationData(feeSummary: FeeSummary): VaFeccCalculationData {
    let calcData = { totalLoanCosts: 0, totalOtherCosts: 0 };

    if (!feeSummary?.closingCost?.byteClosingCost) {
      return calcData;
    }

    calcData = feeSummary.closingCost.byteClosingCost.reduce((calcData, fee) => {
      if (fee.isDeleted) {
        return calcData;
      }

      const borrowerAmount = fee.borrowerAmount || 0;

      if (fee.tridBlock >= 1 && fee.tridBlock <= 3) {
        calcData.totalLoanCosts += borrowerAmount;
      } else {
        calcData.totalOtherCosts += borrowerAmount;
      }

      return calcData;
    }, calcData);

    return calcData;
  }

  getDefaultProviderSettings(): Observable<DefaultProvider> {
    const url = `${this._environment.retail.apiUrl}/ClosingCostWorksheet1dot5/Settings`;
    return this._httpClient.get<SettingsProviderModel>(url).pipe(
      map((data) => {
        data.shoppableProviderSettings.defaultProvider.guid = '';
        return data?.shoppableProviderSettings?.defaultProvider;
      }),
    );
  }

  private _checkIsNAN(value: number): number {
    if (isNaN(value)) {
      return 0;
    }
    return value;
  }
}
